import { useQuery } from '@tanstack/react-query'
import { formatarCnpj, ROTULO_REGIME } from '@via-tax/contracts'
import { Botao, EstadoVazio, Selo } from '@via-tax/ui'
import type { Tela } from '../components/Shell'
import type { ContextoSessao } from '../tipos'
import { useTRPC } from '../trpc'

/**
 * Dashboard na ordem da arquitetura §12.2. Bloco sem dado diz o que vai mostrar
 * e o que falta para chegar lá, em vez de exibir zero — zero pareceria
 * "calculei e deu nada".
 */
export function Dashboard({ contexto, ir }: { contexto: ContextoSessao; ir: (t: Tela) => void }) {
  const trpc = useTRPC()
  const e = contexto.empresaAtiva
  const podeHabilitar = (contexto.capacidadesTenant as string[]).includes('habilitar_empresa')
  const qualidade = useQuery({ ...trpc.documento.qualidade.queryOptions(), enabled: Boolean(e) })
  const cobertura12 = mesesCobertos12(qualidade.data)

  if (!e) {
    return (
      <EstadoVazio
        titulo="Nenhuma empresa selecionada"
        acao={podeHabilitar ? <Botao variante="primario" onClick={() => ir('habilitar')}>Habilitar empresa do Processos</Botao> : undefined}
      >
        {podeHabilitar
          ? 'O Via Tax trabalha com uma empresa por vez. Escolha uma no seletor acima — ou, se ela ainda não aparece, habilite-a a partir do cadastro do Processos.'
          : 'Escolha a empresa no seletor acima. Se ela não aparece, peça ao escritório para habilitá-la no Via Tax.'}
      </EstadoVazio>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="num text-xs text-tinta-3">{formatarCnpj(e.cnpj)}</p>
          <h1 className="text-xl font-semibold tracking-tight">{e.nomeFantasia || e.razaoSocial}</h1>
        </div>
        <div className="flex items-center gap-2">
          {e.regimeTributario ? <Selo tom="navy">{ROTULO_REGIME[e.regimeTributario]}</Selo> : <Selo tom="alerta">Regime não definido</Selo>}
        </div>
      </header>

      <section className="rounded-lg border border-linha bg-painel">
        <div className="grid gap-px overflow-hidden rounded-lg bg-linha md:grid-cols-[1.3fr_1fr]">
          <div className="bg-painel p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3">Impacto estimado em 2027</p>
            <p className="num mt-3 text-3xl font-medium text-tinta-3">—</p>
            <p className="mt-2 max-w-prose text-sm text-tinta-2">
              Débito de CBS sobre as saídas contra o crédito das entradas, com PIS/COFINS extintos. Aparece aqui a partir
              da fase F5, sempre acompanhado da alíquota de referência e da versão da base usadas no cálculo.
            </p>
          </div>
          <div className="bg-painel p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3">Prontidão da base</p>
            <ul className="mt-3 flex flex-col gap-3">
              <li className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1">
                <span className="text-[13px]">Documentos</span>
                <span className="num text-[11px] text-tinta-2">{cobertura12 === null ? '—' : `${cobertura12} de 12 meses`}</span>
                <span className="col-span-2 h-1.5 overflow-hidden rounded-full bg-fundo" aria-hidden>
                  <span
                    className={`block h-full rounded-full ${cobertura12 !== null && cobertura12 >= 12 ? 'bg-ok' : 'bg-navy'}`}
                    style={{ width: `${((cobertura12 ?? 0) / 12) * 100}%` }}
                  />
                </span>
                <span className="col-span-2 text-xs text-tinta-3">
                  {qualidade.data?.documentos.total
                    ? `${qualidade.data.documentos.total.toLocaleString('pt-BR')} documento(s)${qualidade.data.mesesFaltando.length ? ` · faltam ${qualidade.data.mesesFaltando.length} mês(es) no intervalo` : ''}`
                    : 'Nenhum XML enviado ainda'}
                </span>
              </li>
              {PRONTIDAO.map((p) => (
                <li key={p.rotulo} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1">
                  <span className="text-[13px]">{p.rotulo}</span>
                  <span className="num text-[11px] text-tinta-3">{p.fase}</span>
                  <span className="col-span-2 h-1.5 rounded-full bg-fundo" aria-hidden />
                  <span className="col-span-2 text-xs text-tinta-3">{p.falta}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-linha bg-painel p-5">
        <h2 className="text-sm font-semibold">Próximo passo</h2>
        {cobertura12 !== null && cobertura12 >= 12 ? (
          <p className="mt-1 max-w-prose text-sm text-tinta-2">
            Os últimos 12 meses estão cobertos. A revisão do cadastro de produtos (cClassTrib e CST de IBS/CBS) chega na F3.
          </p>
        ) : (contexto.capacidades as string[]).includes('upload_xml') ? (
          <p className="mt-1 max-w-prose text-sm text-tinta-2">
            Enviar os XMLs de entrada e saída dos últimos 12 meses — NF-e, NFC-e, NFS-e, CT-e, energia e telecom, soltos ou
            em ZIP. Confira também se o regime e a área de atuação estão certos: eles decidem o que o simulador vai perguntar.
          </p>
        ) : (
          <p className="mt-1 max-w-prose text-sm text-tinta-2">
            As notas chegam pelo escritório e pelo Portal do Cliente. Enquanto isso, informe o regime dos seus maiores clientes e fornecedores e responda as pendências:
            isso deixa a simulação mais precisa.
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {(contexto.capacidades as string[]).includes('upload_xml') && (
            <Botao variante="primario" onClick={() => ir('enviar')}>
              Enviar XMLs
            </Botao>
          )}
          <Botao onClick={() => ir('documentos')}>Ver documentos recebidos</Botao>
          <Botao variante="fantasma" onClick={() => ir('empresa')}>Revisar dados da empresa</Botao>
        </div>
      </section>
    </div>
  )
}

/** Meses com documento entre os 12 que terminam na última competência recebida. */
function mesesCobertos12(q: { ultimaCompetencia: string | null; competencias: Array<{ competencia: string }> } | undefined): number | null {
  if (!q?.ultimaCompetencia) return null
  let [a, m] = q.ultimaCompetencia.split('-').map(Number) as [number, number]
  const presentes = new Set(q.competencias.map((c) => c.competencia))
  let n = 0
  for (let i = 0; i < 12; i++) {
    if (presentes.has(`${a}-${String(m).padStart(2, '0')}`)) n++
    if (--m < 1) {
      m = 12
      a--
    }
  }
  return n
}

const PRONTIDAO = [
  { rotulo: 'Cadastro de produtos', fase: 'F3', falta: '% da receita com cClassTrib e CST aprovados' },
  { rotulo: 'Fornecedores', fase: 'F4', falta: '% das compras com regime confirmado' },
  { rotulo: 'Pendências abertas', fase: 'F4', falta: 'Perguntas aguardando resposta do cliente' },
]
